// Actionable, user-facing copy for a chat failure, keyed by its code. One source
// of truth so every failure path — backend `error` SSE events and thrown
// HTTP/network errors — speaks the same language and tells the user what to do
// next, instead of surfacing a raw backend message or a generic "failed".
//
// Codes come from two places: the backend `error` event (`token_expired`,
// `agent_failed`, `circular_tool_calls`, plus `stream_dropped` synthesized on a
// dropped connection), and the HTTP/network mapping in useChatSend
// (`not_configured`, `auth_failed`, `network`). An unrecognized code falls back
// to the backend's own message when it gave one, else a generic line.
const COPY_BY_CODE: Record<string, string> = {
  token_expired: "Your LLM key was rejected. Re-enter it and try again.",
  agent_failed: "Istari AI hit an error finishing that response. Try again, or rephrase your request.",
  circular_tool_calls: "Istari AI got stuck repeating the same step. Try rephrasing your request.",
  stream_dropped: "The connection dropped before the reply finished. Please try again.",
  not_configured: "Istari AI isn't set up on this server yet.",
  auth_failed: "Your session was rejected. Sign in again, or re-enter your LLM key.",
  network: "Couldn't reach Istari AI. Check your connection and try again.",
};

export function chatErrorMessage(code: string | undefined, backendDetail?: string | null): string {
  if (code !== undefined && code in COPY_BY_CODE) return COPY_BY_CODE[code];
  const detail = backendDetail?.trim();
  return detail && detail.length > 0 ? detail : "Something went wrong. Please try again.";
}

// The HTTP status of a thrown request mapped to an error code, or undefined when
// no specific code applies (the caller then falls back to the backend detail).
// A null status means the request never got a response (network/DNS/CORS).
export function chatErrorCodeForStatus(status: number | null): string | undefined {
  if (status === null) return "network";
  if (status === 401 || status === 403) return "auth_failed";
  return undefined;
}
